import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CategoryState } from './types';
import { getMessageFromError } from '../../../../common/utils/apiUtils';
import { getCategoriesApiClient } from '../../../../common/apiClients';
import { RootState } from '../../../../app/store';
import {
  CategoriesCreateRequest,
  CategoriesUpdateRequest,
  Category,
  CategoryCreatePayloadRequest,
  CategoryUpdatePayloadRequest
} from '../../../../generated/api';

const sliceName = 'categories';

const initialState: CategoryState = {
  categories: [],
  loading: false
};

export const fetchCategoriesAsync = createAsyncThunk<Category[]>(
  `${sliceName}/fetchCategories`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getCategoriesApiClient();
      return await apiClient.categoriesList();
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createCategoryAsync = createAsyncThunk<Category, CategoryCreatePayloadRequest>(
  `${sliceName}/createCategory`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getCategoriesApiClient();
      const request: CategoriesCreateRequest = { categoryCreatePayloadRequest: arg };
      return await apiClient.categoriesCreate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const updateCategoryAsync = createAsyncThunk<Category, { id: number; data: CategoryUpdatePayloadRequest }>(
  `${sliceName}/updateCategory`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getCategoriesApiClient();
      const request: CategoriesUpdateRequest = { id: arg.id, categoryUpdatePayloadRequest: arg.data };
      return await apiClient.categoriesUpdate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const destroyCategoryAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyCategory`,
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const apiClient = await getCategoriesApiClient();
      await apiClient.categoriesDestroy({ id: arg });
      dispatch(removeCategory(arg));
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const categorySlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    removeCategory: (state: CategoryState, action: PayloadAction<number>): CategoryState => {
      return { ...state, categories: state.categories.filter((c) => c.id !== action.payload) };
    }
  },
  extraReducers: {
    [fetchCategoriesAsync.fulfilled.type]: (state, action: PayloadAction<Category[]>): CategoryState => {
      return { ...state, categories: action.payload, loading: false };
    },
    [createCategoryAsync.fulfilled.type]: (state, action: PayloadAction<Category>): CategoryState => {
      return { ...state, categories: [...state.categories, action.payload], loading: false };
    },
    [updateCategoryAsync.fulfilled.type]: (state, action: PayloadAction<Category>): CategoryState => {
      return {
        ...state,
        categories: state.categories.map((category) => (category.id === action.payload.id ? action.payload : category)),
        loading: false
      };
    }
  }
});

export const selectCategories = (state: RootState): CategoryState => state.categories;
export const { removeCategory } = categorySlice.actions;
export default categorySlice.reducer;
